<template>
    <cod-dialog
        :show.sync="showDialog"
        :drawer="drawer"
        max-width="880px"
    >
        <!-- Navigation Drawer -->
        <div slot="drawer" class="drawer">
            <template v-if="drawer">
                <v-toolbar class="wfull" color="#1565c0" absolute dark flat>
                    <v-btn icon dark @click="options.drawer[drawer].back">
                        <v-icon>fal fa-arrow-left</v-icon>
                    </v-btn>

                    <v-toolbar-title>
                        {{ options.drawer[drawer].title }}
                    </v-toolbar-title>

                    <v-spacer />

                    <v-btn
                        v-for="(option, index) in options.drawer[drawer].primary"
                        :key="index"
                        text
                        dark
                        :disabled="option.disabled"
                        @click="option.onclick"
                    >
                        <v-icon class="mr-3">{{ option.icon }}</v-icon>
                        {{ option.text }}
                    </v-btn>

                    <v-menu
                        v-if="options.drawer[drawer].secondary.length"
                        transition="slide-y-transition"
                        bottom
                    >
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon>
                                <v-icon>far fa-ellipsis-v</v-icon>
                            </v-btn>
                        </template>

                        <v-list class="order-menu">
                            <v-list-item
                                v-for="(option, index) in options.drawer[drawer].secondary"
                                :key="index"
                                :disabled="option.disabled"
                                @click="option.onclick"
                            >
                                <v-list-item-action class="mx-3">
                                    <v-icon :disabled="option.disabled">
                                        {{ option.icon }}
                                    </v-icon>
                                </v-list-item-action>
                                <v-list-item-title>
                                    {{ option.text }}
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-toolbar>

                <div v-if="drawer === 'viewOrder' && selectedOrder" class="drawer-content">
                    <v-slide-y-transition>
                        <div
                            v-if="initPrint && enableReceiptPrinter"
                            class="d-flex align-center flex-column"
                        >
                            <v-checkbox
                                v-model="useReceiptPrinter"
                                :label="$t('general.print_on_receipt_printer')"
                            />

                            <div class="mb-5">
                                <v-btn
                                    class="mx-2"
                                    color="primary"
                                    outlined
                                    @click="initPrint = false"
                                >
                                    <v-icon class="mr-3">fal fa-times</v-icon>
                                    {{ $t('general.cancel') }}
                                </v-btn>

                                <v-btn
                                    class="mx-2"
                                    color="primary"
                                    depressed
                                    @click="printOrder(selectedOrder, useReceiptPrinter)"
                                >
                                    <v-icon class="mr-3">fal fa-check</v-icon>
                                    {{ $t('general.print') }}
                                </v-btn>
                            </div>
                        </div>
                    </v-slide-y-transition>

                    <receipt
                        :order="selectedOrder"
                        :options="{
                            isReceipt: !(locale === 'sr' && selectedOrder.isUnpaid()),
                            isCopy: !selectedOrder.isProforma(),
                            printingSector: null,
                            forceInvoiceForm: forceInvoiceForm,
                        }"
                    />
                </div>

                <div v-else-if="drawer === 'correctOrder' && selectedOrder" class="drawer-content">
                    <correct-order
                        ref="correctOrder"
                        :order="selectedOrder"
                        @enable-issue="enableCorrectedOrderIssue = $event"
                        @create="correctOrder"
                    />
                </div>

                <div v-else-if="drawer === 'paySummary' || drawer === 'correctSummary'" class="drawer-content">
                    <div v-if="summary.client" class="px-4 pt-2 mt-2">
                        {{ $tc('general.buyer', 1) }}:
                        {{ summary.client.name }}
                    </div>
                    <v-alert
                        v-if="summary.client && summary.client.country && summary.client.country !== 'MNE'"
                        class="ma-4"
                        type="warning"
                        text
                        transition="slide-y-transition"
                    >
                        {{ $t('invoice.warning.not_export') }}
                    </v-alert>
                    <v-simple-table>
                        <template v-slot:default>
                            <thead>
                                <tr>
                                    <th>{{ $t('general.receipt_number') }}</th>
                                    <th class="text-right">{{ $t('general.price') }}</th>
                                    <th class="text-right">{{ $t('general.discount') }}</th>
                                    <th class="text-right">{{ $t('general.to_pay') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(order, index) in summary.paysForOrders"
                                    :key="index"
                                >
                                    <td>{{ order.number }}</td>
                                    <td class="text-right">{{ order.getPrice().toFormat() }}</td>
                                    <td class="text-right">{{ order.getDiscountAmount().toFormat() }}</td>
                                    <td class="text-right">{{ order.getToPay().toFormat() }}</td>
                                </tr>
                                <tr class="total">
                                    <td>{{ $t('general.total') }}</td>
                                    <td class="text-right">{{ summary.price.toFormat() }}</td>
                                    <td class="text-right">{{ summary.discountAmount.toFormat() }}</td>
                                    <td class="text-right">{{ summary.toPay.toFormat() }}</td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>

                    <v-row class="pa-5">
                        <v-col cols="12" sm="6">
                            <v-btn
                                block
                                large
                                height="80px"
                                :disabled="paymentDisabled('cash')"
                                @click="payAction('cash')"
                            >
                                <v-icon class="mr-4">fal fa-money-bill</v-icon>
                                {{ $t('general.cash') }}
                            </v-btn>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-btn
                                block
                                large
                                height="80px"
                                :disabled="paymentDisabled('credit_card')"
                                @click="payAction('credit_card')"
                            >
                                <v-icon class="mr-4">fal fa-credit-card</v-icon>
                                {{ $t('general.credit_card') }}
                            </v-btn>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-btn
                                block
                                large
                                height="80px"
                                :disabled="paymentDisabled('invoice')"
                                @click="payAction('invoice')"
                            >
                                <v-icon class="mr-4">fal fa-file-alt</v-icon>
                                {{ $tc('general.invoice_noncash', 1) }}
                            </v-btn>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-btn
                                v-if="locale === 'me'"
                                block
                                large
                                height="80px"
                                :disabled="paymentDisabled('credit_card_business')"
                                @click="payAction('credit_card_business')"
                            >
                                <v-icon class="mr-4">fal fa-credit-card</v-icon>
                                {{ $t('general.credit_card_business') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </div>

                <div v-else-if="drawer === 'creditNote' || drawer === 'summaryCreditNote'" class="drawer-content">
                    <credit-note
                        ref="creditNote"
                        :orders="selectedOrders"
                        :summary="selectMode == 'summaryCreditNote'"
                        @enable-issue="enableCreditNoteIssue = $event"
                        @create="createCreditNote"
                    />
                </div>
            </template>
        </div>

        <!-- Header -->
        <template slot="header">
            {{ options.main.title }}

            <v-spacer />

            <v-btn
                v-for="(option, index) in options.main.primary"
                :key="index"
                text
                dark
                :disabled="option.disabled"
                @click="option.onclick"
            >
                <v-icon class="mr-3">{{ option.icon }}</v-icon>
                {{ option.text }}
            </v-btn>

            <v-menu
                v-if="options.main.secondary.length"
                transition="slide-y-transition"
                bottom
            >
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                        <v-icon>far fa-ellipsis-v</v-icon>
                    </v-btn>
                </template>

                <v-list class="order-menu">
                    <v-list-item
                        v-for="(option, index) in options.main.secondary"
                        :key="index"
                        :disabled="option.disabled"
                        @click="option.onclick"
                    >
                        <v-list-item-action class="mx-3">
                            <v-icon :disabled="option.disabled">
                                {{ option.icon }}
                            </v-icon>
                        </v-list-item-action>
                        <v-list-item-title>
                            {{ option.text }}
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </template>

        <!-- Content -->
        <div slot="content" class="receipts-content">
            <order-list
                ref="orderList"
                :select-mode="selectMode"
                :allowed-select-modes.sync="allowedSelectModes"
                :selected.sync="selectedOrders"
                :users="users"
                :clients="clients"
                :cash-registers="cashRegisters"
                @click="viewOrder"
            />
        </div>
    </cod-dialog>
</template>

<script>
import OrderList from '@/components/OrderList';
import CreditNote from '@/views/shared/components/CreditNote';
import CorrectOrder from '@/views/shared/components/CorrectOrder';
import Receipt from '@/components/print/Receipt';
import CodDialog from '@/components/CodDialog';
import CashRegister from '@/models/CashRegister';
import Order from '@/models/Order';
import User from '@/models/User';
import Client from '@/models/Client';
import AppService from '@/services/AppService';
import OrderService from '@/services/OrderService';
import EinvoiceService from '@/services/EinvoiceService';
import LogService from '@/services/LogService';
import * as mutationTypes from '@/store/mutationTypes';
import { mapGetters } from 'vuex';

export default {
    components: {
        OrderList,
        Receipt,
        CodDialog,
        CreditNote,
        CorrectOrder,
    },

    props: {
        show: {
            type: Boolean,
            default: false
        }
    },

    data: () => ({
        showDialog: false,
        drawer: null,

        selectedOrder: null,
        users: [],
        clients: [],
        cashRegisters: [],

        selectedOrders: [],
        enableCreditNoteIssue: false,
        enableCorrectedOrderIssue: false,
        selectMode: null,
        allowedSelectModes: {
            paySummary: false,
            creditNote: false,
            summaryCreditNote: false,
            advance: false,
        },

        initPrint: false,
        useReceiptPrinter: true,
        receiptPrinter: null,

        forceInvoiceForm: false,
        busy: false
    }),

    watch: {
        show(val) {
            this.showDialog = val;
            this.drawer = null;

            if(val) {
                this.selectMode = null
                this.selectedOrders = []
                this.$store.commit(mutationTypes.SET_CUR_UNPAID_ORDERS, { curUnpaidOrders: [] });

                this.clients = Client.all()
                this.users = User.all()
                this.cashRegisters = CashRegister.all()

                this.receiptPrinter = this.$printService.getAssignedPrinter('receipt')
                this.useReceiptPrinter = this.enableReceiptPrinter

                this.busy = false
            }
        },

        showDialog(val) {
            this.$emit('update:show', val);
        },
    },

    computed: {
        ...mapGetters(['curOrder', 'trainingMode']),

        options() {
            const cancelSelection = {
                available: this.selectMode,
                text: this.$t('general.cancel'),
                icon: 'fal fa-ban',
                disabled: false,
                onclick: () => this.selectMode = null
            }

            const proceedWithSelection = {
                available: this.selectMode,
                text: this.$t('general.continue'),
                icon: 'fal fa-check',
                disabled: !this.selectedOrders.length,
                onclick: this.proceedWithSelection
            }

            const paySummary = {
                available: !this.selectMode && !this.trainingMode && this.allowedSelectModes.paySummary,
                text: this.$t('general.pay_summary'),
                icon: 'fal fa-check',
                disabled: false,
                onclick: () => this.selectMode = 'paySummary'
            }

            const creditNote = {
                available: true,
                text: this.$t('general.credit_note'),
                icon: 'fal fa-pen',
                disabled: !this.allowedSelectModes.creditNote || !!this.selectMode || this.trainingMode,
                onclick: () => this.selectMode = 'creditNote'
            }

            const summaryCreditNote = {
                available: true,
                text: this.$t('general.credit_note_summary'),
                icon: 'fal fa-edit',
                disabled: !this.allowedSelectModes.summaryCreditNote || !!this.selectMode || this.trainingMode,
                onclick: () => this.selectMode = 'summaryCreditNote'
            }

            const advance = {
                available: locale === 'sr',
                text: this.$t('general.advance_close'),
                icon: 'fal fa-calendar-times',
                disabled: !this.allowedSelectModes.advance || !!this.selectMode || this.trainingMode,
                onclick: () => this.selectMode = 'advance'
            }

            const cancelOrder = {
                available: !(!isAllegra && this.selectedOrder?.isUnpaid() && !this.allows('allow_cancel_order')) && this.selectedOrder?.allowCancel(),
                text: this.selectedOrder?.isEinvoice()
                    ? this.$t('einvoice.storno')
                    : this.$t('general.cancel'),
                icon: 'fal fa-times',
                disabled: this.busy || this.selectedOrder?.corrected(),
                onclick: this.cancelOrder
            }

            const printOrder = {
                available: (locale !== 'sr' && locale !== 'ba') || !this.selectedOrder?.isFiscal(),
                text: this.$t('general.print'),
                icon: 'fal fa-print',
                disabled: false,
                onclick: this.togglePrint
            }

            const proformaPay = {
                available: this.selectedOrder && this.selectedOrder.isProforma() && this.selectedOrder.getToPay().toUnit() > 0,
                text: this.$t('general.issue_receipt'),
                icon: 'fal fa-file-export',
                disabled: false,
                onclick: this.proformaPay
            }

            const correctOrder = {
                available: this.selectedOrder?.allowCorrect(),
                text: this.$t('general.correct'),
                icon: 'fal fa-pen',
                disabled: false,
                onclick: () => this.drawer = 'correctOrder'
            }

            const changeFormat = {
                available: this.selectedOrder && this.selectedOrder.isFiscal(),
                text: this.forceInvoiceForm ? this.$t('general.format_regular') : this.$t('general.format_wide'),
                icon: this.forceInvoiceForm ? 'fal fa-receipt' : 'fal fa-file',
                disabled: (locale === 'sr' && this.selectedOrder?.isUnpaid()) || this.selectedOrder?.isCreditNote(),
                onclick: () => this.forceInvoiceForm = !this.forceInvoiceForm
            }

            const correctSummary = {
                available: this.selectedOrder && this.selectedOrder.allowCorrectSummary(),
                text: this.$t('general.correct_payment'),
                icon: 'fal fa-pen',
                disabled: false,
                onclick: () => this.drawer = 'correctSummary'
            }

            const copyOrder = {
                available: this.selectedOrder && this.selectedOrder.allowCopy(),
                text: this.$t('general.issue_receipt_copy'),
                icon: 'fal fa-copy',
                disabled: false,
                onclick: this.copyOrder
            }

            const advanceNew = {
                available: this.selectedOrder && this.selectedOrder.allowAdvanceNew(),
                text: this.$t('general.advance_issue_new'),
                icon: 'fal fa-file-plus',
                disabled: false,
                onclick: this.advanceNew
            }

            const advancePay = {
                available: this.selectedOrder && this.selectedOrder.allowAdvancePay(),
                text: this.$t('general.advance_issue_receipt'),
                icon: 'fal fa-file-export',
                disabled: false,
                onclick: this.advancePay
            }

            const einvoiceFromFiscal = {
                available: this.selectedOrder && this.selectedOrder.allowEinvoiceFromFiscal(),
                text: this.$t('general.einvoice_issue_for_fiscal'),
                icon: 'fal fa-file-contract',
                disabled: !this.isOnline,
                onclick: this.einvoiceFromFiscal
            }

            const receiptLink = {
                available: false && this.selectedOrder && this.canExportReceipt,
                text: this.$t('general.receipt_link'),
                icon: 'fal fa-link',
                disabled: false,
                onclick: this.copyReceiptLink
            }

            const saveAsPdf = {
                available: this.selectedOrder && this.canExportReceipt,
                text: this.$t('general.save_as_pdf'),
                icon: 'fal fa-file-pdf',
                disabled: false,
                onclick: this.saveAsPdf
            }

            const confirmCorrect = {
                available: true,
                text: this.$t('general.confirm'),
                icon: 'fal fa-check',
                disabled: !this.enableCorrectedOrderIssue,
                onclick: () => this.$refs.correctOrder.correctOrder()
            }

            const creditNoteIssue = {
                available: true,
                text: this.$t('general.issue'),
                icon: 'fal fa-check',
                disabled: !this.enableCreditNoteIssue,
                onclick: () => this.$refs.creditNote.createCreditNote()
            }

            return {
                main: {
                    title: !this.einvoiceOnly ? this.$tc('general.receipt', 2) : this.$tc('general.invoice', 2),
                    primary: [
                        cancelSelection,
                        proceedWithSelection,
                        paySummary
                    ].filter(o => o.available),
                    secondary: [
                        creditNote,
                        summaryCreditNote,
                        advance
                    ].filter(o => o.available),
                },
                drawer: {
                    viewOrder: {
                        title: this.viewOrderTitle,
                        back: () => this.drawer = null,
                        primary: [
                            cancelOrder,
                            printOrder
                        ].filter(o => o.available),
                        secondary: [
                            proformaPay,
                            correctOrder,
                            changeFormat,
                            correctSummary,
                            copyOrder,
                            advanceNew,
                            advancePay,
                            einvoiceFromFiscal,
                            receiptLink,
                            saveAsPdf,
                        ].filter(o => o.available)
                    },
                    correctOrder: {
                        title: this.viewOrderTitle,
                        back: () => this.drawer = 'viewOrder',
                        primary: [confirmCorrect].filter(o => o.available),
                        secondary: []
                    },
                    correctSummary: {
                        title: this.$t('general.pay_summary'),
                        back: () => this.drawer = null,
                        primary: [],
                        secondary: []
                    },
                    paySummary: {
                        title: this.$t('general.pay_summary'),
                        back: () => this.drawer = null,
                        primary: [],
                        secondary: []
                    },
                    creditNote: {
                        title: this.$t('general.credit_note'),
                        back: () => this.drawer = null,
                        primary: [creditNoteIssue].filter(o => o.available),
                        secondary: []
                    },
                    summaryCreditNote: {
                        title: this.$t('general.credit_note'),
                        back: () => this.drawer = null,
                        primary: [creditNoteIssue].filter(o => o.available),
                        secondary: []
                    }
                }
            }
        },

        curCashRegister() {
            return CashRegister.current();
        },

        orderEmpty() {
            return !this.curOrder || !this.curOrder.items || !this.curOrder.items.length;
        },

        viewOrderTitle() {
            if(!this.selectedOrder) {
                return ''
            }

            if(this.selectedOrder.isProforma()) {
                return this.$tc('general.proforma', 1)
            }

            if(this.selectedOrder.getPaidInvoice().toUnit() > 0) {
                return this.$tc('general.invoice_noncash', 1)
            }

            return this.$tc('general.receipt', 1)
        },

        enableReceiptPrinter() {
            return !!this.receiptPrinter && this.device.platform !== 'web'
        },

        noncashOnly() {
            return this.tenant && this.tenant.venue && !!this.tenant.venue.settings.noncash_only
        },

        summary() {
            let orders = []
            if(this.drawer === 'viewOrder' || this.drawer === 'correctSummary') {
                orders = this.selectedOrder.paysForOrders()
            } else {
                orders = this.selectedOrders
            }

            return {
                client: orders.length ? orders[0].client : null,
                paysForOrders: orders,
                price: orders.reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0)),
                discountAmount: orders.reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0)),
                toPay: orders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0))
            }
        },

        canExportReceipt() {
            if(!this.selectedOrder) return false;

            if(locale === 'sr') {
                return this.selectedOrder.register_status === 'success'
                    && !!this.selectedOrder.register_request
                    && !!this.selectedOrder.register_response
            }

            return true;
        },

        einvoiceOnly() {
            return EinvoiceService.einvoiceOnly();
        }
    },

    methods: {
        viewOrder(order) {
            this.drawer = 'viewOrder';
            this.selectedOrder = order;
            this.forceInvoiceForm = order.getPaidInvoice().toUnit() != 0
                || order.isProforma()
                || !order.isFiscal()
            this.initPrint = false;
        },

        togglePrint() {
            if(!this.enableReceiptPrinter || this.selectedOrder?.isEinvoice()) {
                this.printOrder(this.selectedOrder, false)
                return
            }

            if(this.device.platform === 'android') {
                this.printOrder(this.selectedOrder, true)
                return
            }

            this.initPrint = !this.initPrint
        },

        printOrder(order, useReceiptPrinter) {
            this.initPrint = false

            this.$printService.printReceipt(order, {
                isCopy: true,
                choosePrinter: !useReceiptPrinter,
                forceInvoiceForm: this.forceInvoiceForm,
            })

            LogService.info({
                type: 'copy_receipt',
                value: order.getToPay().abs().toFormat('0.00') || '0.00',
                receiptHash: order.hash,
            });
        },

        proceedWithSelection() {
            if(this.selectMode === 'advance') {
                if(this.selectedOrders[0].isFiscal()) {
                    this.closeAdvance(this.selectedOrders);
                    return;
                }

                if(this.selectedOrders[0].isEinvoice()) {
                    const order = Order.createFromAdvance(this.selectedOrders, { user: this.user, anotherAdvance: false })
                    this.eventBus.$emit('issue-receipt', order);
                    this.showDialog = false;
                    return;
                }
            }

            this.drawer = this.selectMode
        },

        paymentDisabled(type) {
            if(this.drawer !== 'correctSummary') return false

            if(!this.selectedOrder) return true

            if(this.noncashOnly && (type === 'cash' || type === 'credit_card')) return true

            const paidByType = Money.parse(this.selectedOrder['paid_' + type])
            if(paidByType.toUnit() == this.selectedOrder.getToPay().toUnit()) return true

            return false
        },

        async payAction(method) {
            if(this.drawer === 'correctSummary') {
                this.correctSummary(method)
                return
            }

            this.showDialog = false

            this.$store.commit(mutationTypes.SET_CUR_UNPAID_ORDERS, { curUnpaidOrders: this.selectedOrders });

            this.eventBus.$emit('order-init-action', {
                paymentType: method,
                value: 0,
                mode: 'summary',
                internalNumber: method === 'invoice' ? '' + OrderService.getNextInternalNumber() : null
            });
        },

        async proformaPay() {
            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            if((locale === 'sr' && !this.einvoiceOnly) || !isAllegra) {
                this.$confirm(
                    this.$t('order.proforma_pay_warning'),
                    {
                        buttonTrueText: this.$t('general.confirm'),
                        buttonCancelText: this.$t('general.cancel'),
                        icon: 'fal fa-cash-register',
                        color: 'info',
                        title: this.$t('general.confirm')
                    },
                    async result => {
                        if (!result) return;

                        const orderResult = await this.selectedOrder.$duplicate().proformaPay({ user: this.user })
                        this.onOrderIssue(orderResult, this.$t('order.issued'))
                    }
                )

                return;
            }

            if(!this.orderEmpty) {
                this.$snackbar(this.$t('allegra.order.another_active'), 'error');
                return
            }

            this.showDialog = false

            const order = this.selectedOrder.fresh(this.user);
            this.eventBus.$emit('issue-receipt', order);
        },

        async advancePay() {
            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            if(!this.orderEmpty) {
                this.$snackbar(this.$t('allegra.order.another_active'), 'error');
                return
            }

            this.showDialog = false

            const order = Order.createFromAdvance(this.selectedOrder, { user: this.user, anotherAdvance: false })
            this.eventBus.$emit('issue-receipt', order)
        },

        async advanceNew() {
            if(!this.orderEmpty) {
                this.$snackbar(this.$t('allegra.order.another_active'), 'error');
                return
            }

            this.showDialog = false

            const order = Order.createFromAdvance(this.selectedOrder, { user: this.user, anotherAdvance: true })
            this.eventBus.$emit('issue-receipt', order)
        },

        correctOrder(data) {
            if(!data.items.length) return

            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            const clientNeeded = locale === 'sr' && !this.selectedOrder.client_id;

            let additionalOptions = {};
            if(clientNeeded) {
                additionalOptions = {
                    formFields: [{
                        type: 'select',
                        key: 'client',
                        label: this.$t('general.select_customer'),
                        itemText: 'name',
                        items: Client.all(),
                        searchBy: ['name', 'id_number'],
                        validation: 'required'
                    }]
                }
            }

            this.$confirm(
                this.$t('order.correct_warning'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.cancel'),
                    icon: 'fal fa-cash-register',
                    color: 'error',
                    title: this.$t('general.confirm'),
                    ...additionalOptions
                },
                async result => {
                    const proceed = result?.hasOwnProperty('confirm') ? result.confirm : result;
                    if (!proceed) return;

                    const client = clientNeeded ? result.data.client : null;

                    const orderResult = await this.selectedOrder.$duplicate().correct({ user: this.user, client: client, items: data.items })
                    this.onOrderIssue(orderResult, this.$t('order.corrected'))
                }
            )
        },

        cancelOrder() {
            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            const clientNeeded = locale === 'sr' && !this.selectedOrder.isEinvoice() && !this.selectedOrder.isUnpaid() && !this.selectedOrder.client_id;
            const reasonNeeded = locale === 'sr' && !this.selectedOrder.isEinvoice() && !this.selectedOrder.isUnpaid();
            const commentNeeded = locale === 'sr' && this.selectedOrder.isEinvoice() && !this.selectedOrder.isUnpaid();

            const originalUser = this.selectedOrder.getUser()
            const clearOriginalUserDebtNeeded = this.user?.id !== originalUser?.id
                && originalUser
                && !this.allows('allow_cancel_order', originalUser)
                && this.allows('allow_clear_debt_all')
                && this.selectedOrder?.isCashExchanged()
                && !isAllegra

            let additionalOptions = { formFields: [] };

            if(clientNeeded) {
                additionalOptions.formFields.push({
                    type: 'select',
                    key: 'client',
                    label: this.$t('general.select_customer'),
                    itemText: 'name',
                    items: Client.all(),
                    searchBy: ['name', 'id_number'],
                    validation: 'required_without_all:error',
                    onChange: ($event, values) => {
                        if(values.client) {
                            values.error = false
                        }
                    }
                })
            }

            if(commentNeeded) {
                additionalOptions.formFields.push({
                    type: 'text',
                    key: 'comment',
                    label: this.$t('einvoice.cancel_reason'),
                    validation: 'required'
                })
            }

            if(reasonNeeded) {
                additionalOptions.formFields.push({
                    type: 'checkbox',
                    key: 'error',
                    label: this.$t('order.error.general'),
                    validation: clientNeeded ? 'required_without_all:client' : null,
                    onChange: ($event, values) => {
                        if(values.error) {
                            values.client = null
                        }
                    }
                })
            }

            if(clearOriginalUserDebtNeeded) {
                additionalOptions.formFields.push({
                    type: 'checkbox',
                    key: 'clearOriginalUserDebt',
                    label: this.$t('staff.clear_debt_original_user'),
                    default: true
                })
            }

            this.$confirm(
                !this.selectedOrder.isEinvoice()
                    ? this.$t('order.cancel_warning')
                    : this.$t('einvoice.storno_warning'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.cancel'),
                    icon: 'fal fa-cash-register',
                    color: 'error',
                    title: this.$t('general.confirm'),
                    width: 360,
                    ...additionalOptions
                },
                async result => {
                    const proceed = result?.hasOwnProperty('confirm') ? result.confirm : result;
                    if (!proceed) return;

                    const client = clientNeeded ? result.data.client : null;
                    const comment = commentNeeded ? result.data.comment : null;
                    const flag = result?.data?.error ? 'reason,error' : null;
                    const clearOriginalUserDebt = clearOriginalUserDebtNeeded ? !!result?.data?.clearOriginalUserDebt : false;

                    this.busy = true

                    const orderResult = await this.selectedOrder.$duplicate().cancel({ user: this.user, client, comment, flag }, clearOriginalUserDebt)
                    if(this.selectedOrder.paysForOrders()) {
                        this.eventBus.$emit('orders-refresh');
                    }

                    this.onOrderIssue(orderResult, this.$t('order.canceled'))

                    this.busy = false
                }
            )
        },

        copyOrder() {
            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            this.$confirm(
                this.$t('order.copy_format'),
                {
                    buttonTrueText: this.$t('general.format_regular'),
                    buttonFalseText: this.$t('general.format_wide'),
                    buttonFalseColor: '#1565C0',
                    buttonCancelText: null,
                    icon: 'fal fa-cash-register',
                    color: 'info',
                    title: this.$t('general.confirm')
                },
                async result => {
                    if (result === null) return;

                    const forceInvoiceForm = !result
                    const choosePrinter = forceInvoiceForm

                    const orderResult = await this.selectedOrder.$duplicate().copy({ user: this.user, forceInvoiceForm, choosePrinter })
                    this.onOrderIssue(orderResult, this.$t('order.copied'))
                }
            )
        },

        correctSummary(method) {
            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            this.$confirm(
                this.$t('order.correct_warning'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.cancel'),
                    icon: 'fal fa-cash-register',
                    color: 'error',
                    title: this.$t('general.confirm')
                },
                async result => {
                    if (!result) return;

                    const orderResult = await this.selectedOrder.$duplicate().correctSummary(this.user, method)
                    this.onOrderIssue(orderResult, this.$t('order.corrected'))
                }
            )
        },

        createCreditNote(data) {
            if(!data.orders.length || !data.items.length) return

            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            this.$confirm(
                this.$t('order.credit_note_warning'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.cancel'),
                    icon: 'fal fa-cash-register',
                    color: 'error',
                    title: this.$t('general.confirm')
                },
                async result => {
                    if (!result) return;

                    const orderResult = await Order.createCreditNote(data.orders, data.items, {
                        user: this.user,
                        ...data.options
                    })

                    this.onOrderIssue(orderResult, this.$t('general.credit_note_issued'))
                }
            )
        },

        closeAdvance(orders) {
            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            const firstOrderWithClient = orders.find(o => !!o.client_id);
            let client = firstOrderWithClient ? firstOrderWithClient.getClient() : null
            const clientNeeded = locale === 'sr' && !client;

            let additionalOptions = {};
            if(clientNeeded) {
                additionalOptions = {
                    formFields: [{
                        type: 'select',
                        key: 'client',
                        label: this.$t('general.select_customer'),
                        itemText: 'name',
                        items: Client.all(),
                        searchBy: ['name', 'id_number'],
                        validation: 'required'
                    }]
                }
            }

            this.$confirm(
                this.$t('order.advance_close_warning'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.cancel'),
                    icon: 'fal fa-cash-register',
                    color: 'error',
                    title: this.$t('general.confirm'),
                    ...additionalOptions
                },
                async result => {
                    const proceed = result?.hasOwnProperty('confirm') ? result.confirm : result;
                    if (!proceed) return;

                    const client = clientNeeded ? result.data.client : null;

                    this.selectMode = null;

                    const orderResult = await Order.closeAdvance(orders, { user: this.user, client: client, closeWith: 'single' })
                    this.onOrderIssue(orderResult, this.$t('order.advance_closed'))
                }
            )
        },

        einvoiceFromFiscal() {
            const buyer = this.selectedOrder?.getClient();
            const jbkjs = !!(buyer?.id_type === 'tax_id_number_local_jbkjs')

            const vatObligations = [...(!this.selectedOrder.isAdvance() ? ['by_delivery_date', 'by_issue_date'] : []), 'by_due_date', 'no_obligation'].map(obligation => {
                return {
                    value: obligation,
                    display: this.selectedOrder.isAdvance() && obligation === 'by_due_date'
                        ? this.$t(`einvoice.vat_obligation.by_due_date_advance`)
                        : this.$t(`einvoice.vat_obligation.${obligation}`)
                }
            })

            const formFields = [{
                type: 'text',
                key: 'contract_number',
                label: this.$t('einvoice.contract_number'),
                validation: jbkjs ? 'required_without_all:invoice_number,agreement_number' : null
            },{
                type: 'text',
                key: 'invoice_number',
                label: this.$t('einvoice.invoice_number'),
                validation: jbkjs ? 'required_without_all:contract_number,agreement_number' : null
            },{
                type: 'text',
                key: 'agreement_number',
                label: this.$t('einvoice.agreement_number'),
                validation: jbkjs ? 'required_without_all:contract_number,invoice_number' : null
            },{
                type: 'text',
                key: 'receipt_number',
                label: this.$t('einvoice.receipt_number')
            },{
                type: 'text',
                key: 'dispatch_number',
                label: this.$t('einvoice.dispatch_number')
            },{
                type: 'text',
                key: 'project_number',
                label: this.$t('einvoice.project_number')
            },{
                type: 'text',
                key: 'reference_number',
                label: this.$t('einvoice.reference_number')
            },{
                type: 'text',
                key: 'reference_number_model',
                label: this.$t('einvoice.reference_number_model')
            },{
                type: 'select',
                key: 'vat_obligation',
                label: this.$t('einvoice.vat_obligation.title'),
                itemText: 'display',
                itemKey: 'value',
                items: vatObligations,
                default: this.selectedOrder.isAdvance() ? 'by_due_date' : 'by_delivery_date',
                searchBy: ['display'],
                validation: 'required'
            },{
                type: 'text',
                key: 'delivery_at',
                label: this.$t('einvoice.delivery_at'),
                validation: 'date:DD.MM.YYYY.|required',
                mask: '##.##.####.',
                default: moment().format('DD.MM.YYYY'),
            },{
                type: 'text',
                key: 'due_date',
                label: !this.selectedOrder.isAdvance() ? this.$t('general.due_date') : this.$t('general.paid_date_advance'),
                validation: 'date:DD.MM.YYYY.|required_if:vat_obligation,by_due_date',
                mask: '##.##.####.'
            },{
                type: 'file',
                key: 'attachments',
                label: this.$tc('einvoice.attachments', 1)
            }];

            if(jbkjs) {
                formFields.push({
                    type: 'checkbox',
                    key: 'crf',
                    label: this.$t('einvoice.crf')
                })
            }

            this.$confirm(
                this.$t('einvoice.fiscal_info') + '<br><br>' + this.$t('einvoice.fiscal_warning'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.cancel'),
                    icon: 'fal fa-file-contract',
                    color: 'error',
                    title: this.$t('general.confirm'),
                    width: 565,
                    formFields: formFields,
                },

                async result => {
                    if (!result.confirm) return;

                    if(result.data.delivery_at) {
                        result.data.delivery_at = moment(result.data.delivery_at, 'DD.MM.YYYY.').format('YYYY-MM-DD')
                    }

                    let dueDate = null
                    if(result.data.due_date) {
                        dueDate = moment(result.data.due_date, 'DD.MM.YYYY.').format('YYYY-MM-DD')
                        delete result.data.due_date
                    }

                    if(!result.data.attachments) result.data.attachments = []

                    const orderResult = await this.selectedOrder.$duplicate().einvoiceFromFiscal({
                        user: this.user,
                        registerRequest: result.data,
                        dueDate,
                    })

                    this.onOrderIssue(orderResult, this.$t('einvoice.issued'))
                }
            )
        },

        /**
         * On order issue
         */
        onOrderIssue(result, successMsg) {
            if(result.status === Order.SYNC_STATUS_FAILED) {
                this.$confirm(result.error, {
                    icon: 'fal fa-exclamation-triangle',
                    color: 'error',
                    title: this.$t('order.error.title'),
                }, async result => {});

                return
            }

            if(result.status === Order.SYNC_STATUS_SUCCESS_WITH_ERRORS) {
                this.$confirm(result.error || result.efiError || result.printError, {
                    icon: 'fal fa-exclamation-circle',
                    color: 'warning',
                    title: successMsg,
                }, async result => {});
            }

            if(result.status === Order.SYNC_STATUS_SUCCESS) {
                this.$snackbar(successMsg, 'success', { timeout: 3000 })

                if(result?.order?.isUnpaid()) {
                    LogService.info({
                        type: 'cancel_order',
                        value: result?.order?.getToPay().abs().toFormat('0.00') || '0.00'
                    });
                }
            }

            this.drawer = null
            this.selectMode = null

            if(this.$refs.orderList) {
                this.$refs.orderList.getOrders()
            }

            this.eventBus.$emit('orders-updated')
        },

        copyReceiptLink() {
            const appUrl = AppService.getAppUrl()
            const hash = this.selectedOrder.getLinkHash();

            const url = `${appUrl}/#/invoice/${hash}`;

            navigator.clipboard.writeText(url).then(() => {
                this.$snackbar(this.$t('general.receipt_link_copied'), 'success')
            }, (err) => {
                this.$snackbar(this.$t('general.receipt_link_copy_error'), 'success')
            });
        },

        saveAsPdf() {
            if(!this.selectedOrder) return false;

            this.$printService.printReceipt(this.selectedOrder, {
                isCopy: true,
                choosePrinter: false,
                forceInvoiceForm: this.forceInvoiceForm,
                saveAsPdfFile: locale === 'sr' && this.selectedOrder.register_number
                    ? this.selectedOrder.register_number
                    : `${this.$tc('general.receipt', 1)}-${this.selectedOrder.number}`
            })
        }
    }
};
</script>

<style lang="scss" scoped>
.tabs {
    height: 100%;

    ::v-deep .v-tabs-bar {
        height: 100%;

        .tab {
            margin-left: auto !important;
            height: 100%;
        }
    }
}

.drawer {
    height: 100%;

    .drawer-content {
        height: 100%;
        max-height: 100%;
        padding-top: 64px;

        @media screen and (max-width: 960px) {
            padding-top: 56px;
        }

        .total {
            background: #fafafa;
            font-weight: bold;

            td {
                border-bottom: 1px solid #e0e0e0;
            }
        }
    }
}

.receipts-content {
    min-height: 600px;
}
</style>
